/**
 * Enhanced geolocation service for Capacitor + web.
 * 
 * Improvements over base implementation:
 *   - Retry logic with exponential backoff
 *   - Cached last known position
 *   - Better timeout handling
 *   - High accuracy fallback
 *   - Graceful degradation
 *   - Detailed error reporting
 */
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

const STORAGE_KEY = 'noor_last_location_cache';
const RETRY_BACKOFF = [1000, 2000, 3000]; // ms — reduced from [2000, 5000, 10000]

export class GeolocationError extends Error {
  constructor(kind, message) {
    super(message);
    this.kind = kind;
  }
}

/**
 * Load cached last known position from localStorage.
 * Returns null if no cache or cache expired (older than 30 days).
 */
function getCachedLocation() {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (!cached) return null;
    const data = JSON.parse(cached);
    const age = Date.now() - (data.timestamp || 0);
    const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 days
    if (age > maxAge) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

/**
 * Save location to localStorage cache.
 */
function setCachedLocation(lat, lng) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      lat, lng, timestamp: Date.now(),
    }));
  } catch { /* ignore */ }
}

export const geolocationService = {
  isSupported() {
    return Capacitor.isNativePlatform?.() || (typeof navigator !== 'undefined' && !!navigator.geolocation);
  },

  /**
   * Get cached location if available.
   */
  getCachedLocation() {
    return getCachedLocation();
  },

  /**
   * Returns the OS permission state without prompting.
   */
  async getPermissionState() {
    if (Capacitor.isNativePlatform?.()) {
      try {
        const { location } = await Geolocation.checkPermissions();
        return location === 'granted' ? 'granted'
             : location === 'denied' ? 'denied'
             : 'prompt';
      } catch (e) {
        return 'unknown';
      }
    }
    if (typeof navigator !== 'undefined' && navigator.permissions?.query) {
      try {
        const r = await navigator.permissions.query({ name: 'geolocation' });
        return r.state; // 'granted' | 'denied' | 'prompt'
      } catch {
        return 'unknown';
      }
    }
    return 'unknown';
  },

  /**
   * Request current position with retry logic.
   * Falls back to cached location on repeated failures.
   *
   * @param {object} opts - { timeout: 12000, highAccuracy: false, maxRetries: 2 }
   * @returns {Promise<{lat, lng, accuracy}>}
   * @throws {GeolocationError}
   */
  async requestCurrent({
    timeout = 12000,
    highAccuracy = false,
    maxRetries = 2,
  } = {}) {
    let lastError = null;

    if (Capacitor.isNativePlatform?.()) {
      
      // Step 1: Check current permission state
      let permState = 'unknown';
      try {
        const check = await Geolocation.checkPermissions();
        permState = check.location;
      } catch (e) {
        // silent
      }

      // Step 2: If not granted, request permissions
      if (permState !== 'granted') {
        try {
          const perm = await Geolocation.requestPermissions({ permissions: ['location'] });
          if (perm.location === 'denied') {
            throw new GeolocationError('denied', 'Permission refusée');
          }
          if (perm.location !== 'granted') {
            throw new GeolocationError('denied', 'Permission non accordée');
          }
        } catch (e) {
          if (e instanceof GeolocationError) throw e;
          throw new GeolocationError('unavailable', e?.message || 'Erreur permission');
        }
      }

      // Retry loop for native
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const posPromise = new Promise(async (resolve, reject) => {
            try {
              const result = await Geolocation.getCurrentPosition({
                enableHighAccuracy: highAccuracy,
                timeout: Math.min(timeout, 10000),
              });
              resolve(result);
            } catch (e) {
              reject(e);
            }
          });

          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => {
              reject(new Error('Geolocation timeout'));
            }, timeout)
          );

          const pos = await Promise.race([posPromise, timeoutPromise]);
          
          const result = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          };
          setCachedLocation(result.lat, result.lng);
          return result;
        } catch (e) {
          lastError = e;
          const msg = String(e?.message || '').toLowerCase();
          if (msg.includes('timeout') || msg.includes('position unavailable')) {
            if (attempt < maxRetries) {
              const waitMs = RETRY_BACKOFF[attempt] || 3000;
              await new Promise((resolve) => setTimeout(resolve, waitMs));
              continue;
            }
            const cached = getCachedLocation();
            if (cached) {
              return cached;
            }
            throw new GeolocationError('timeout', 'La géolocalisation a expiré après plusieurs tentatives');
          }
          throw new GeolocationError('unavailable', e?.message || 'Position indisponible');
        }
      }
    }

    // Fallback to web geolocation
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      throw new GeolocationError('unavailable', 'Géolocalisation non supportée par ce navigateur');
    }

    return new Promise((resolve, reject) => {
      let completed = false;
      const timeoutId = setTimeout(() => {
        if (!completed) {
          completed = true;
          const cached = getCachedLocation();
          if (cached) {
            console.log('[Geolocation] Using cached location');
            resolve(cached);
          } else {
            reject(new GeolocationError('timeout', 'La géolocalisation a expiré'));
          }
        }
      }, timeout);

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (completed) return;
          completed = true;
          clearTimeout(timeoutId);
          const result = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          };
          setCachedLocation(result.lat, result.lng);
          console.log('[Geolocation] Web geolocation SUCCESS:', result);
          resolve(result);
        },
        (err) => {
          if (completed) return;
          completed = true;
          clearTimeout(timeoutId);
          if (err.code === err.PERMISSION_DENIED) {
            reject(new GeolocationError('denied', 'Permission refusée'));
          } else if (err.code === err.TIMEOUT) {
            const cached = getCachedLocation();
            if (cached) {
              resolve(cached);
            } else {
              reject(new GeolocationError('timeout', 'La géolocalisation a expiré'));
            }
          } else {
            reject(new GeolocationError('unavailable', err.message || 'Position indisponible'));
          }
        },
        {
          enableHighAccuracy: highAccuracy,
          timeout,
          maximumAge: 5 * 60 * 1000, // Accept 5-minute old positions
        },
      );
    });
  },

  /**
   * Watch position (continuous updates).
   * Useful for travel detection on PrayerTimes page.
   *
   * @returns {function} unwatch callback
   */
  watchPosition(
    onSuccess,
    onError = null,
    options = { enableHighAccuracy: false, timeout: 15000, maximumAge: 10000 },
  ) {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      if (onError) onError(new GeolocationError('unavailable', 'Géolocalisation non supportée'));
      return () => {};
    }

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        onSuccess({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      (err) => {
        if (onError) {
          if (err.code === err.PERMISSION_DENIED) {
            onError(new GeolocationError('denied', 'Permission refusée'));
          } else if (err.code === err.TIMEOUT) {
            onError(new GeolocationError('timeout', 'La géolocalisation a expiré'));
          } else {
            onError(new GeolocationError('unavailable', err.message));
          }
        }
      },
      options,
    );

    return () => navigator.geolocation.clearWatch(watchId);
  },

  /**
   * Open system settings on native platforms.
   * (Requires a 3rd-party plugin in real app)
   */
  async openSystemSettings() {
    if (!Capacitor.isNativePlatform?.()) return false;
    try {
      // In production, use @capacitor-community/app-launcher or similar
      // to open app settings. For now, this is informational.
    } catch { /* ignore */ }
    return false;
  },
};
